import {crossroads} from 'crossroads';
import {IRouteDefinition} from '../index';
import {findRouteDefinitionByHashPattern} from '../constants/routeDefinitionsData';
import {getRouteDefinition} from '../constants/routeLookup';
import {expandPageShellTabRoutes} from './pageShellTabRoutes';
import {splitHashPathAndQuery} from './hashQuery';

/**
 * Map a Crossroads route pattern to an IRouteDefinition.
 * Skips RegExp-only catch-alls (e.g. http400). Expanded PageShell tab hashes are not on
 * raw RouteDefinition entries — resolve them via expandPageShellTabRoutes (which already
 * copies the shell's hashPrefixOrgId onto each tab routeDef).
 *
 * Lives above RouteDefinitions + pageShellTabRoutes so those modules stay one-way
 * (expand → lookup; this file → both) without a cycle.
 */
function resolveRouteDefFromCrossroadsPattern(pattern: string | RegExp): IRouteDefinition | undefined {
    if (typeof pattern !== 'string') {
        return undefined;
    }
    const fromDefs = findRouteDefinitionByHashPattern(pattern);
    if (fromDefs) {
        return getRouteDefinition(fromDefs) ?? undefined;
    }
    const expanded = expandPageShellTabRoutes().find(
        (e) => typeof e.hash === 'string' && e.hash === pattern,
    );
    return expanded ? getRouteDefinition(expanded.routeDef) ?? undefined : undefined;
}

type CrossroadsRouteLike = {
    _priority?: number;
    priority?: number;
    _matchRegexp: RegExp;
    _pattern: string | RegExp;
    _getParamsObject?: (request: string) => Record<string, unknown>;
};

/** Normalize a location/hasher hash to a Crossroads path (no `#/`, no query). */
function hashToPath(hash: string): string {
    const stripped = hash.replace(/^(?:#\/)?/, '').replace(/^#/, '');
    return splitHashPathAndQuery(stripped).path;
}

/**
 * Best registered Crossroads match for a hash, with parsed route params (includes `{orgnr}`).
 * Used when boot must read org from standalone deep links like `formview/{orgnr}/…`.
 */
export function getRouteMatchFromHash(hash: string): {
    routeDef: IRouteDefinition;
    params: Record<string, unknown>;
} | null {
    const path = hashToPath(hash);
    const routes = (crossroads as { _routes?: CrossroadsRouteLike[] })._routes ?? [];
    let bestResolved: IRouteDefinition | undefined;
    let bestRoute: CrossroadsRouteLike | undefined;
    let bestPriority = -Infinity;
    for (const cr of routes) {
        const re = cr._matchRegexp;
        if (!re) {
            continue;
        }
        const prevIndex = re.lastIndex;
        const matched = re.test(path);
        re.lastIndex = prevIndex;
        if (!matched) {
            continue;
        }
        const priority = cr._priority ?? cr.priority ?? 0;
        if (priority < bestPriority) {
            continue;
        }
        const resolved = resolveRouteDefFromCrossroadsPattern(cr._pattern);
        if (!resolved) {
            continue;
        }
        if (priority > bestPriority || !bestResolved) {
            bestResolved = resolved;
            bestRoute = cr;
            bestPriority = priority;
        }
    }
    if (!bestResolved || !bestRoute) {
        return null;
    }
    const params = bestRoute._getParamsObject?.(path) ?? {};
    return {routeDef: bestResolved, params};
}

/** Resolve the best registered Crossroads match for a URL hash to an IRouteDefinition. */
export function getRouteDefFromHash(hash: string): IRouteDefinition | undefined {
    return getRouteMatchFromHash(hash)?.routeDef;
}
